/**
 * @fileoverview Radio group for selecting companion case option.
 *
 * Three mutually exclusive choices:
 * 1. No companions - This case has no related companions
 * 2. Start group - Start a new companion group (become primary)
 * 3. Link existing - Link to an existing case
 */
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export type CompanionChoice = 'no_companions' | 'start_group' | 'link_existing';

interface CompanionOptionSelectorProps {
  value: CompanionChoice | null;
  onChange: (value: CompanionChoice) => void;
  disabled?: boolean;
}

export const CompanionOptionSelector: React.FC<CompanionOptionSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <RadioGroup
      value={value ?? undefined}
      onValueChange={(v) => onChange(v as CompanionChoice)}
      disabled={disabled}
      className="space-y-2"
    >
      <div className="flex items-start gap-2">
        <RadioGroupItem value="no_companions" id="no_companions" className="mt-0.5" />
        <div className="flex-1">
          <Label
            htmlFor="no_companions"
            className="text-xs font-medium text-zinc-900 cursor-pointer"
          >
            No companions
          </Label>
          <p className="text-xs text-zinc-500 leading-snug">
            This case has no related companions
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <RadioGroupItem value="start_group" id="start_group" className="mt-0.5" />
        <div className="flex-1">
          <Label
            htmlFor="start_group"
            className="text-xs font-medium text-zinc-900 cursor-pointer"
          >
            This is the first case
          </Label>
          <p className="text-xs text-zinc-500 leading-snug">
            Start a new companion group (this case becomes primary)
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <RadioGroupItem value="link_existing" id="link_existing" className="mt-0.5" />
        <div className="flex-1">
          <Label
            htmlFor="link_existing"
            className="text-xs font-medium text-zinc-900 cursor-pointer"
          >
            Link to another case
          </Label>
          <p className="text-xs text-zinc-500 leading-snug">
            Link to an existing case from this firm
          </p>
        </div>
      </div>
    </RadioGroup>
  );
};
